import React, { memo, useMemo } from 'react';

import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import MilestoneStatusCellRenderer from 'app/pages/loanPipelines/LoanPipelinesTable/common/LoanPipelinesCellRenderer/MilestoneStatusCellRenderer';
import PropTypes from 'prop-types';

import { TagType } from 'constants/tag';

import { getChannelId, getChannelLoanHeader, isExternalChannelId } from '../TeamChat.utils';

import ExternalChannelBadge from './components/ExternalChannelBadge';
import { OPEN_LOAN_TOOLTIP } from './TeamChatChannelTitle.constants';
import {
  borrowerStyles,
  clickableLoanNumberBadgeStyles,
  fallbackStyles,
  loanNumberStyles,
  milestoneSlotStyles,
  rootStyles,
} from './TeamChatChannelTitle.styles';
import { getBorrowerName, getMilestoneValue } from './TeamChatChannelTitle.utils';

const TeamChatChannelTitle = memo(
  ({ channel, fallbackName = '', isUnread = false, onLoanNumberClick = null }) => {
    const loanHeader = getChannelLoanHeader(channel);
    const borrowerName = getBorrowerName(loanHeader);
    const loanNumber = loanHeader?.loanNumber ?? null;
    const titleVariant = isUnread ? 'bold14' : 'medium14';
    const isExternal = isExternalChannelId(getChannelId(channel));

    const milestoneValue = useMemo(() => getMilestoneValue(loanHeader), [loanHeader]);

    if (!loanHeader || (!loanNumber && !milestoneValue && !borrowerName)) {
      return (
        <Box sx={rootStyles}>
          <Typography sx={fallbackStyles} variant={titleVariant}>
            {fallbackName}
          </Typography>
          {isExternal && <ExternalChannelBadge />}
        </Box>
      );
    }

    const showLoanNumberAsBadge = !!loanNumber && !!onLoanNumberClick;

    return (
      <Box sx={rootStyles}>
        {!!loanNumber && !showLoanNumberAsBadge && (
          <Typography sx={loanNumberStyles} variant={titleVariant}>
            {loanNumber}
          </Typography>
        )}
        {showLoanNumberAsBadge && (
          <Tooltip title={OPEN_LOAN_TOOLTIP}>
            <ButtonBase
              aria-label={OPEN_LOAN_TOOLTIP}
              component="button"
              sx={clickableLoanNumberBadgeStyles}
              onClick={onLoanNumberClick}
            >
              <Typography variant="regular12">{`#${loanNumber}`}</Typography>
            </ButtonBase>
          </Tooltip>
        )}
        {isExternal && <ExternalChannelBadge />}
        {!!milestoneValue && (
          <Box sx={milestoneSlotStyles}>
            <MilestoneStatusCellRenderer
              isNameKey
              type={TagType.SMALL_FAT}
              value={milestoneValue}
            />
          </Box>
        )}
        {!!borrowerName && (
          <Typography sx={borrowerStyles} variant={titleVariant}>
            {borrowerName}
          </Typography>
        )}
      </Box>
    );
  },
);

TeamChatChannelTitle.propTypes = {
  channel: PropTypes.object.isRequired,
  fallbackName: PropTypes.string,
  isUnread: PropTypes.bool,
  onLoanNumberClick: PropTypes.func,
};

export default TeamChatChannelTitle;
